import { FacilityStatus } from "encamp-prisma/generated/browser";

/** Short chip label for a co-located site: "Co-located – HIL1 DC1". */
export function getCoLocatedShortLabel(
  primary?: { name?: string | null; customerFacilityId?: string | null } | null
): string {
  const code = primary?.customerFacilityId?.trim() || primary?.name?.trim();
  return code ? `Co-located – ${code}` : "Co-located";
}

/** Full sentence for a co-located site naming the primary facility. */
export function getCoLocatedSentence(
  primary?: { name?: string | null; customerFacilityId?: string | null } | null
): string {
  const name = primary?.name?.trim();
  return name
    ? `Co-located with ${name} on the same site`
    : "Co-located with another reporting facility on the same site";
}

/** Format a date (Date or ISO string) for NNR reason text (e.g. "Closed as of 12/10/2024"). */
function formatReasonDate(date: Date | string): string {
  const parsed = typeof date === "string" ? new Date(date) : date;
  return parsed.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

export type FormattedNotReportingReasonOptions = {
  reportingYear?: number;
};

/**
 * Minimal facility shape for deriving a not-reporting reason. `status` is a string so callers
 * holding EITHER the Prisma `FacilityStatus` enum (backend) OR the Oak GraphQL `FacilityStatus`
 * enum (frontend) can pass it directly — both share the same string values. Dates accept a
 * `Date` (backend Prisma) or an ISO `string` (Oak GraphQL).
 *
 * NAMING THE CO-LOCATION PRIMARY: `getCoLocatedSentence` / `getCoLocatedShortLabel` name the primary
 * only when `colocationReportingFacility` is hydrated. To get the named copy, a caller's Prisma
 * query must select it, e.g. `colocationReportingFacility: { select: { name: true, customerFacilityId: true } }`.
 * Callers that pass a bare facility row (no include) intentionally fall back to the generic
 * "Co-located …" copy — the NNR letter/email dataloaders (`encamp-communication` email/mail),
 * `newlyNotReportingFacilities` (api resolver + staff-report lambda), and the oneSchema facility
 * parser do this today. Hydrate the relation in those queries if/when the named copy is wanted there.
 */
export type NotReportingReasonFacility = {
  status?: string | null;
  isNotReporting?: boolean | null;
  isNotReportingThroughEncamp?: boolean | null;
  colocationReportingFacilityId?: string | null;
  colocationReportingFacility?: {
    name?: string | null;
    customerFacilityId?: string | null;
  } | null;
  dateClosed?: Date | string | null;
  dateInactive?: Date | string | null;
};

/**
 * Returns the full sentence reason for newly-not-reporting emails (e.g. "Closed as of 12/10/2024"
 * or "Closed for the entire reporting year"). Matches the copy in the NNR cover letter reasons block.
 * Use when building TemplateFacility.newlyNotReportingReason.
 */
export function getFormattedOakNotReportingReason(
  facility?: NotReportingReasonFacility,
  options?: FormattedNotReportingReasonOptions
): string | null {
  if (!facility) return null;

  const reportingYear = options?.reportingYear;
  const fallbackSuffix = reportingYear != null ? " for the entire reporting year" : "";

  if (facility.status === FacilityStatus.CLOSED) {
    if (facility.dateClosed) {
      return `Closed as of ${formatReasonDate(facility.dateClosed)}`;
    }
    return `Closed${fallbackSuffix}`.trim() || "Closed";
  }

  if (facility.status === FacilityStatus.INACTIVE) {
    if (facility.dateInactive) {
      return `Inactive as of ${formatReasonDate(facility.dateInactive)}`;
    }
    return `Inactive${fallbackSuffix}`.trim() || "Inactive";
  }

  if (facility.colocationReportingFacilityId) {
    return getCoLocatedSentence(facility.colocationReportingFacility);
  }

  if (facility.isNotReportingThroughEncamp) {
    return "No longer reporting through Encamp";
  }

  if (facility.isNotReporting) {
    return "Chemical inventory was below reporting thresholds for the entire reporting year";
  }

  return null;
}

export function getOakNotReportingReason(facility?: NotReportingReasonFacility) {
  if (!facility) return null;

  if (facility.status === FacilityStatus.CLOSED) {
    return "Closed";
  }

  if (facility.status === FacilityStatus.INACTIVE) {
    return "Is Inactive";
  }

  if (facility.colocationReportingFacilityId) {
    return getCoLocatedShortLabel(facility.colocationReportingFacility);
  }

  if (facility.isNotReportingThroughEncamp) {
    return "Without Encamp";
  }

  if (facility.isNotReporting) {
    return "Under Thresholds";
  }

  return null;
}

/**
 * Short display label for a STORED `notReportingReason`. Fall back to
 * `getOakNotReportingReason(facility)` when null (historical, un-backfilled reports).
 * EXEMPT has no facility-derived equivalent, so it only surfaces here.
 */
export function formatStoredNotReportingReason(reason?: string | null): string | null {
  switch (reason) {
    case "CLOSED":
      return "Closed";
    case "COLOCATED":
      return "Co-located";
    case "UNDER_THRESHOLD":
      return "Under Thresholds";
    case "INACTIVE":
      return "Is Inactive";
    case "NOT_WITH_ENCAMP":
      return "Without Encamp";
    case "EXEMPT":
      return "Exempt";
    default:
      return null;
  }
}

/**
 * Full-sentence version of a STORED not-reporting reason. For CLOSED/INACTIVE this mirrors
 * the date-aware copy of `getFormattedOakNotReportingReason` (e.g. "Closed as of 12/10/2024"
 * / "Closed for the entire reporting year") using the same `facility.dateClosed` /
 * `facility.dateInactive` and `reportingYear` inputs, so preferring the stored reason loses
 * no regulatory wording. EXEMPT has no facility field to derive from; its note is appended
 * when present.
 */
export function getFormattedStoredNotReportingReason(
  reason?: string | null,
  note?: string | null,
  facility?: NotReportingReasonFacility,
  reportingYear?: number
): string | null {
  const fallbackSuffix = reportingYear != null ? " for the entire reporting year" : "";
  switch (reason) {
    case "CLOSED":
      if (facility?.dateClosed) {
        return `Closed as of ${formatReasonDate(facility.dateClosed)}`;
      }
      return `Closed${fallbackSuffix}`.trim() || "Closed";
    case "COLOCATED":
      return getCoLocatedSentence(facility?.colocationReportingFacility);
    case "UNDER_THRESHOLD":
      return "Chemical inventory was below reporting thresholds for the entire reporting year";
    case "INACTIVE":
      if (facility?.dateInactive) {
        return `Inactive as of ${formatReasonDate(facility.dateInactive)}`;
      }
      return `Inactive${fallbackSuffix}`.trim() || "Inactive";
    case "NOT_WITH_ENCAMP":
      return "No longer reporting through Encamp";
    case "EXEMPT":
      return note?.trim()
        ? `Exempt from Tier II reporting requirements: ${note.trim()}`
        : "Exempt from Tier II reporting requirements";
    default:
      return null;
  }
}

/**
 * Full-sentence not-reporting reason. Prefers the STORED reason (the explicit per-year source
 * of truth, and the only source for EXEMPT), falling back to deriving from live facility fields
 * only for historical / un-backfilled reports with no stored reason. Because the stored
 * formatter is now date-aware (it takes the same `facility` dates + `reportingYear`), preferring
 * the stored reason keeps the "Closed as of <date>" regulatory copy. Single source of truth
 * shared by the comms dataloaders and the report Not Reporting page.
 */
export function getResolvedNotReportingReasonSentence(args: {
  storedReason?: string | null;
  storedNote?: string | null;
  facility?: NotReportingReasonFacility;
  reportingYear?: number;
}): string | null {
  // `storedReason` is the Prisma enum value as a string here (not the GraphQL enum), so the
  // stored formatter's literal comparisons are intentional — this shared helper does not
  // import GraphQL types.
  return (
    getFormattedStoredNotReportingReason(
      args.storedReason,
      args.storedNote,
      args.facility,
      args.reportingYear
    ) ?? getFormattedOakNotReportingReason(args.facility, { reportingYear: args.reportingYear })
  );
}

/**
 * Short chip/label for a not-reporting reason, naming the co-location primary when
 * available. Prefers the STORED reason and falls back to deriving from live
 * facility fields for historical / un-backfilled reports. The single source of
 * truth for the short label so the Reports grid, PDF generator, and CLI never
 * diverge on whether a co-located site names its primary.
 */
export function getResolvedNotReportingReasonShortLabel(args: {
  storedReason?: string | null;
  facility?: NotReportingReasonFacility;
}): string | null {
  // `storedReason` is the Prisma enum value as a string (not the GraphQL enum);
  // the literal comparison is intentional — this shared module imports no GraphQL types.
  if (args.storedReason === "COLOCATED") {
    return getCoLocatedShortLabel(args.facility?.colocationReportingFacility);
  }
  // Historical reports with no stored reason: derive from facility fields (which
  // also names the co-location primary via the colocationReportingFacilityId branch).
  return (
    formatStoredNotReportingReason(args.storedReason) ?? getOakNotReportingReason(args.facility)
  );
}
